// Read-only WebMCP tools for AI agents in the browser: https://developer.chrome.com/docs/ai/webmcp
// The API is experimental, so the tools are registered only where the browser provides it.
// Forms are exposed to agents declaratively with the toolname/tooldescription attributes.

interface IWebMcpTool {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  annotations?: { readOnlyHint?: boolean }
  execute: (input: Record<string, unknown>) => Promise<string>
}

interface IModelContext {
  registerTool: (tool: IWebMcpTool) => void
}

interface IOrganizationRates {
  title: string
  url: string
  created_at: string
  rates: Record<string, string> | null
}

type TNbkrRates = Record<string, [number, string, number]>

const CURRENCIES = ['usd', 'eur', 'rub', 'kzt', 'cny']

const getModelContext = (): IModelContext | undefined => {
  const withContext = (target: object) => (target as { modelContext?: IModelContext }).modelContext
  return withContext(document) ?? withContext(navigator)
}

const toNumber = (value: string | undefined) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export default defineNuxtPlugin(() => {
  const modelContext = getModelContext()
  if (!modelContext) return

  const siteUrl = window.location.origin

  modelContext.registerTool({
    name: 'get_exchange_rates',
    description:
      'Get today\'s exchange rates of a currency to the Kyrgyz som (KGS): the official NBKR rate and the cash buy/sell rates in every bank of Kyrgyzstan, with the best rates first.',
    inputSchema: {
      type: 'object',
      properties: {
        currency: {
          type: 'string',
          enum: CURRENCIES,
          description: 'Currency code: usd, eur, rub, kzt or cny.'
        }
      },
      required: ['currency']
    },
    annotations: { readOnlyHint: true },
    async execute(input) {
      const currency = String(input.currency ?? '').toLowerCase()
      if (!CURRENCIES.includes(currency)) {
        return `Unsupported currency. Use one of: ${CURRENCIES.join(', ')}.`
      }

      const [organizations, nbkr] = await Promise.all([
        $fetch<{ data?: IOrganizationRates[] }>('/api/rates/organizations-cached'),
        $fetch<TNbkrRates>('/api/rates/nbkr-cached')
      ])

      const banks = (organizations.data ?? [])
        .map((bank) => ({
          bank: bank.title,
          url: `${siteUrl}${bank.url}/rates`,
          buy: toNumber(bank.rates?.[`buy_${currency}`]),
          sell: toNumber(bank.rates?.[`sell_${currency}`]),
          updated_at: bank.created_at
        }))
        .filter((bank) => bank.buy || bank.sell)

      const bestBuy = [...banks].sort((a, b) => (b.buy ?? 0) - (a.buy ?? 0))[0]
      const bestSell = [...banks]
        .filter((bank) => bank.sell)
        .sort((a, b) => (a.sell ?? 0) - (b.sell ?? 0))[0]

      return JSON.stringify({
        currency: currency.toUpperCase(),
        unit: 'KGS per 1 unit of currency',
        nbkr_official_rate: nbkr[currency] ? Number(nbkr[currency][1]) : null,
        best_bank_buys_at: bestBuy ?? null,
        best_bank_sells_at: bestSell ?? null,
        banks,
        source: `${siteUrl}/rates`
      })
    }
  })

  modelContext.registerTool({
    name: 'convert_currency',
    description:
      'Convert an amount between the Kyrgyz som (KGS) and foreign currencies at the official NBKR rate of today. Bank rates differ: use get_exchange_rates for them.',
    inputSchema: {
      type: 'object',
      properties: {
        amount: { type: 'number', description: 'Amount to convert.' },
        from: { type: 'string', description: 'Currency code to convert from, for example kgs, usd, eur, rub, kzt.' },
        to: { type: 'string', description: 'Currency code to convert to, for example kgs, usd, eur, rub, kzt.' }
      },
      required: ['amount', 'from', 'to']
    },
    annotations: { readOnlyHint: true },
    async execute(input) {
      const amount = Number(input.amount)
      const from = String(input.from ?? '').toLowerCase()
      const to = String(input.to ?? '').toLowerCase()
      if (!Number.isFinite(amount)) return 'Amount must be a number.'

      const nbkr = await $fetch<TNbkrRates>('/api/rates/nbkr-cached')
      // Som value of one unit of the currency
      const rateOf = (code: string) => (code === 'kgs' ? 1 : nbkr[code] ? Number(nbkr[code][1]) : null)
      const fromRate = rateOf(from)
      const toRate = rateOf(to)
      if (!fromRate || !toRate) {
        return `Unsupported currency. Use kgs or one of: ${Object.keys(nbkr).join(', ')}.`
      }

      return JSON.stringify({
        amount,
        from: from.toUpperCase(),
        to: to.toUpperCase(),
        result: Math.round(((amount * fromRate) / toRate) * 100) / 100,
        rate_source: 'NBKR official rate',
        source: `${siteUrl}/rates/nbkr`
      })
    }
  })
})
